import type { ProductsData, ProductsPayload } from '@/app/api/search/products/createProductsData.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { getSearchParamsFromQueryString } from '@/app/common/urlParametersUtils.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { productsDataDefault } from '@/app/components/search/common/reloadData/productsDataDefault.ts';
import {
  MAP_STYLE_TYPE,
  MAP_STYLES,
  MAPBOX_ACCESS_TOKEN,
  MAPBOX_ACCESS_TOKEN_WHITELABEL,
} from '@/app/components/search/components/SearchPageContainer/MapContainer/types.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import type { SearchParams } from '@/server/searchParams.ts';
import {
  type Product,
  type ProductAccommodation,
  PROMOTION_TYPES,
  type TransportAirport,
} from '@/domainLogic/index.ts';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, {
  Layer,
  type MapRef,
  Marker,
  type MarkerEvent,
  NavigationControl,
  Popup,
  ScaleControl,
  AttributionControl,
  Source,
} from 'react-map-gl/mapbox';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import {
  CARD_TYPE,
  resultClickedEvents,
} from '@/app/components/search/components/SearchPageContainer/resultClickedEvents.ts';
import { ReloadType } from '@/app/components/search/utils';
import { hotelTopSecretCircleLayerStyle, tshAreaCenter } from '@/utils/mapUtils.ts';
import { getAdultsChildrenCount } from '@/utils/peopleUtils.ts';
import { createNewSearchParamsBase } from '@/app/components/search/common/reloadData/createNewSearchParams.ts';
import { useSearchCommonPageData } from '@/app/components/search/searchCommonPageProvider.tsx';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import type { Feature, GeoJsonProperties, Point } from 'geojson';
import type {
  MarkerClickProps,
  ViewStateChangeEventWithOriginal,
} from '@/app/components/search/components/SearchPageContainer/MapContainer/MapComponent/types.ts';
import { DESTINATION_TYPE } from '@/app/search/types.ts';
import { throttleFn } from '@/utils/debounce.ts';
import { isUsLocale } from '@/app/common/measures/locale.ts';
import { SortValues } from '@/app/common/memsearch/types.ts';
import EvolutionCard from '@/app/components/search/components/EvolutionCard';
import { createResultPageMapActionPayload } from '@/app/common/starTrack/events/resultPageMapAction';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { isFocusLost } from '@/app/common/utils/focus.ts';
import usePriceMode from '@/app/components/search/hooks/usePriceMode.ts';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import clientAPICall from '../../../../common/clientAPICall.ts';
import getSearchParams from '../../../../common/getSearchParams';
import { APIStatusEnum } from '../../../../common/reloadData/APIStatusEnum';
import handleMapLoad from '../../../../common/reloadData/handleMapLoad.ts';
import { ClickEventNameEnum } from '../../../../Curiosity/Analytics/utils/ClickEventNameEnum';
import { getIsTransportEnabled, noop } from '../../../../Utilities';
import type { DynamicContentResponse, SkiLift } from '../../../../utils/api/filtersAPIResponseTypes';
import type { DealCard } from '../../../../utils/buildDealCards/dealCardTypes';
import { sumClasses } from '../../../../utils/misc';
import {
  hasAirportsAndStations,
  hasAirportsOnly,
  hasAirportsOrStations,
  hasStationsOnly,
} from '../../../../utils/productsResponse';
import { CardsContext } from '../../ListContainer/CardsGrid/CardsContext';
import type { Dot } from '../../ListContainer/CardsGrid/types';
import {
  changeMapLanguage,
  handleMarkerSelections,
  MAP_DOT_INDEX,
  markerSelection,
  markMarkerWithOpenPopup,
} from '../utils';
import AirportPopup from './AirportComponent/AirportPopup';
import { createAirportPopupProps } from './AirportComponent/AirportPopup/utils';
import getAllGeoJSON from './getAllGeoJSON';
import { layerStyle } from './getAllGeoJSON/getDotsGeoJSON';
import getPopupCardProps, { calculateCardWidth, type PopupCardProps } from './MarkerComponent/getPopupCardProps';
import Price from './MarkerComponent/Price';
import Promotion from './MarkerComponent/Promotion';
import { MarkerContainer } from './MarkerComponent/styled.ts';
import { getDotPrice, getPromotionConfig } from './MarkerComponent/utils.tsx';
import SkiliftMarker from './SkiliftMarker';
import { BlankContainer } from './styled';

const Card = dynamic(() => import('../../../Card'), { ssr: false });
const MarkerComponent = dynamic(() => import('./MarkerComponent'), { ssr: false });
const AirportComponent = dynamic(() => import('./AirportComponent'), { ssr: false });
const MapControls = dynamic(() => import('./MapControls'), { ssr: false });

type PopupCardPropsByHotelId = {
  hotelId: number;
  props: PopupCardProps;
  visible: boolean;
};

const popupCardPropsExist = (popupCardPropsByHotel: PopupCardPropsByHotelId[], hotelId: number): boolean =>
  !!popupCardPropsByHotel.find((p) => p.hotelId === hotelId);

const createInitialMapZoom = (initialHighlightHotel: boolean, destinationType: DESTINATION_TYPE | undefined) => {
  if (initialHighlightHotel) return 15;
  if (destinationType === DESTINATION_TYPE.TAG) return 8;
  if (destinationType === DESTINATION_TYPE.CITY) return 12;
  return undefined;
};

export type InitialHighlightHotel = {
  cheapestMealPlanFinalPrice: number;
  currency: string;
  handleOtherHotelChosen: () => void;
  isTopSecret?: boolean;
} & ProductAccommodation;

export type MapComponentProps = {
  mapRef: React.RefObject<MapRef | null>;
  showFilters: boolean;
  isLoading: boolean;
  expand: boolean;
  isWaiting: boolean;
  mapContainerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  setExpand: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMapOnTop: React.Dispatch<React.SetStateAction<boolean>>;
  cardOverHotelId: number | undefined;
  dynamicContentResponse?: DynamicContentResponse;
  showCloseResize?: boolean;
  isDialog?: boolean;
  initialHighlightHotel?: InitialHighlightHotel;
  setCardOverHotelId?: React.Dispatch<React.SetStateAction<number | undefined>>;
  shouldLaunchSearchChanged: boolean;
};

const MapComponent = ({
  mapRef,
  showFilters,
  isLoading,
  expand,
  setExpand,
  isWaiting,
  mapContainerRef,
  setIsMapOnTop,
  cardOverHotelId,
  dynamicContentResponse,
  showCloseResize,
  isDialog = false,
  initialHighlightHotel,
  shouldLaunchSearchChanged,
}: MapComponentProps) => {
  const markerRef = useRef<HTMLDivElement>(undefined);
  const { searchInfoData, searchInfoDetailData, localization, curiosityParams, commonData } = usePageWrapperData();
  const { lat: searchLat, lon: searchLon, productsData: searchProductsData } = useSearchPageData();
  const { isMobile, isDesktop, starTrack, handleHotelChosen, handleSearchChanged, isEvoLayout } = useCommonClient();
  const { totalPriceMode, showTotalPrice: prefersTotalPrice } = usePriceMode();
  // Dots follow the funnel total-price mode, so a family search now shows the group total on them.
  const showTotalPriceOnDots = totalPriceMode && prefersTotalPrice;
  const { t } = useTranslation();
  const { curiosityClickEvents } = useCuriosityManager();

  const { vcSearchId } = searchProductsData;
  const productsData: ProductsData = useMemo(() => {
    let data = searchProductsData.dealCards
      ? searchProductsData
      : initialHighlightHotel
        ? getEmptyProductDataForInitialHighlightHotel(initialHighlightHotel)
        : searchProductsData;

    if (
      initialHighlightHotel &&
      data.dealCards &&
      data.dealCards.findIndex((card) => card.product.accommodation.hotelId === initialHighlightHotel.hotelId) === -1
    ) {
      data = {
        ...data,
        dealCards: [...data.dealCards, getEmptyDealCardForInitialHighlightHotel(initialHighlightHotel)],
      };
    }

    return data;
  }, [searchProductsData, initialHighlightHotel]);

  const { dealCards = [] } = productsData;

  const lat = initialHighlightHotel ? initialHighlightHotel?.latitude : searchLat;
  const lon = initialHighlightHotel ? initialHighlightHotel?.longitude : searchLon;
  const coordinates = {
    lat,
    lon,
  };
  // MPH-1048: opening the viewport on a Top Secret hotel's own coordinates puts the building in the
  // middle of the screen, which identifies it as plainly as a marker would. The map opens on the
  // same offset point the area circle is drawn around, so the property is somewhere inside that
  // circle and nowhere in particular on screen.
  const initialCenter = useMemo(
    () =>
      initialHighlightHotel?.isTopSecret
        ? tshAreaCenter({ latitude: lat, longitude: lon })
        : { latitude: lat, longitude: lon },
    [initialHighlightHotel?.isTopSecret, lat, lon]
  );
  const productsResultsBeforeMapFilterCnt = productsData.productCounters?.productsResultsBeforeMapFilterCnt || 0;
  const isTransportEnabled = getIsTransportEnabled(productsData);
  const locationSearch = canUseDOM ? window.location.search : '';
  const searchParams = useMemo(() => getSearchParamsFromQueryString(locationSearch), [locationSearch]);
  const travellersCount = useMemo(() => getAdultsChildrenCount(searchParams), [searchParams]);
  const {
    isWhiteLabel,
    configValuesApp: { localizeMap, lastMinuteDeals },
  } = localization;
  const mapboxAccessToken = isWhiteLabel ? MAPBOX_ACCESS_TOKEN_WHITELABEL : MAPBOX_ACCESS_TOKEN;

  const hasResults = initialHighlightHotel || (dealCards && dealCards.length > 0);
  const results: DealCard[] = useMemo(() => [...dealCards].reverse(), [dealCards]);
  const [popupCardPropsByHotelId, setPopupCardPropsByHotelId] = useState<PopupCardPropsByHotelId[]>([]);
  const [hotelIdLastMarkerClicked, setHotelIdLastMarkerClicked] = useState<number>(0);
  const [airportSelected, setAirportSelected] = useState<TransportAirport | undefined>();
  const [selectedSkilift, setSelectedSkilift] = useState<SkiLift | undefined>();
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [mapStyleType, setMapStyleType] = useState<MAP_STYLE_TYPE>(MAP_STYLE_TYPE.DEFAULT);
  const [zoomLevel, setZoomLevel] = useState<number>(13);
  const [resultDot, setResultDot] = useState<Dot | undefined>(undefined);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const { highlightedHotelId } = useSearchDealsPageData();
  const mouseOnMarker = useRef(false);
  const { handleMapChanged } = useSearchCommonPageData();
  const initialMapZoom = createInitialMapZoom(
    !!initialHighlightHotel,
    searchInfoDetailData?.destination?.destinationType
  );

  const handleResetMap = () => {
    setIsResizing(true);
    const searchParamsResetMap = getSearchParams({ exclude: [URLParameters.MAP_AREA] });
    const newSearchParams = createNewSearchParamsBase({ searchParams: searchParamsResetMap });

    handleSearchChanged({ newSearchParams });
  };

  const hideAllPopupCards = () => {
    setPopupCardPropsByHotelId((prevState) => prevState.map((p) => ({ ...p, visible: false })));
  };

  const showPopupCard = (hotelId: number, props?: PopupCardProps) => {
    setPopupCardPropsByHotelId((prevState) => {
      const popupCardProps = prevState.find((p) => p.hotelId === hotelId);
      if (popupCardProps) {
        return prevState.map((p) =>
          p.hotelId === hotelId
            ? { ...p, props: props || popupCardProps.props, visible: true }
            : { ...p, visible: false }
        );
      }
      if (props) {
        return prevState
          .map((p) => ({ ...p, visible: false }))
          .concat({
            hotelId,
            props,
            visible: true,
          });
      }
      return prevState;
    });
    handleMarkerSelections(hotelId);
  };

  const createMapSearchUrlParam = (): SearchParams => {
    const bounds = mapRef?.current?.getBounds();
    const northWest = bounds?.getNorthWest();
    const southEast = bounds?.getSouthEast();
    return mapRef && mapRef.current && northWest && southEast
      ? { [URLParameters.MAP_AREA]: `MAPSEARCH,${northWest.lat},${northWest.lng},${southEast.lat},${southEast.lng}` }
      : {};
  };

  const { dotsGeoJSON, minLng, minLat, maxLng, maxLat } = useMemo(
    () => getAllGeoJSON({ results, productsData }),
    [results, productsData]
  );

  const resultTSHCardOver =
    !isMobile && cardOverHotelId
      ? results.find(
          (deal) => deal?.product?.rate?.flags?.tsh && deal?.product?.accommodation?.hotelId === cardOverHotelId
        )
      : undefined;

  const handlePopupCardHotelChosen = (chosenHotelId: string, dealCard: DealCard | undefined) => {
    if (initialHighlightHotel?.handleOtherHotelChosen) {
      initialHighlightHotel.handleOtherHotelChosen();
    }
    const { clickId } = handleHotelChosen(chosenHotelId, {
      openInNewTab: true,
      ...(dealCard ? { dealCard } : {}),
    });
    resultClickedEvents({
      starTrack,
      cardType: CARD_TYPE.MAP,
      dealCard,
      clickId,
      vcSearchId,
      commonData,
      curiosityParams,
      localization,
    });
  };

  const markerClick = ({ hotelId, cityId, latMarker, lonMarker, price, count = 0 }: MarkerClickProps) => {
    if (!isMobile) {
      setResultDot(undefined);
    }

    if (count === 0) {
      setHotelIdLastMarkerClicked(hotelId);
      if (popupCardPropsExist(popupCardPropsByHotelId, hotelId)) {
        showPopupCard(hotelId);
        return;
      }
      // Stop polling for results for any previously clicked markers. Just poll for the last one clicked.
    } else if (hotelId !== hotelIdLastMarkerClicked) return;

    const searchParamsFresh = getSearchParams();

    // Popup Loading state
    const cardWidth = calculateCardWidth(isMobile);
    showPopupCard(hotelId, {
      cardWidth,
      isDot: true,
      coordinates: {
        lng: lonMarker,
        lat: latMarker,
      },
      mapProps: {
        dealCard: {
          index: 999,
          product: {} as Product,
          deepLink: '',
          promotionTypes: [],
          promotionTheme: null,
        },
        isOverlay: true,
        dealCardCtaCallBack: noop,
      },
      searchParams: searchParamsFresh,
      isWaiting: true,
      isLoading: true,
    });

    // starTrack.hotelMapDotClicked({
    //   searchId: searchParams[URLParameters.SEARCH_ID],
    //   internalHotelId: hotelId.toString(),
    //   totalPrice: parseInt(price.toString().replace(/\D/g, ''), 10),
    //   currency,
    // });

    const productsPayload: ProductsPayload = {
      searchParams: {
        ...searchParamsFresh,
        [URLParameters.HIGHLIGHT_HOTEL_ID]: `${hotelId}`,
        [URLParameters.LIMIT]: '1',
        [URLParameters.OFFSET]: '0',
        [URLParameters.PAGE]: '1',
      } as SearchParams,
      isSearchFromServer: false,
      commonData,
      curiosityParams,
      localization,
    };

    clientAPICall<ProductsPayload, ProductsData>({
      apiName: API_NAME.PRODUCTS,
      payload: productsPayload,
      commonData,
      traceParent: searchInfoData.searchTraceParent,
      defaultResponse: {
        ...productsDataDefault,
        apiStatus: APIStatusEnum.ERROR,
      },
    }).then((res) => {
      if (res?.apiStatus === APIStatusEnum.WAIT && count <= 60) {
        setTimeout(() => {
          markerClick({ hotelId, cityId, latMarker, lonMarker, price, count: count + 1 });
        }, 1000);
      } else {
        hideAllPopupCards();
        if (res?.apiStatus === APIStatusEnum.READY) {
          const popupDealCard = { ...res?.dealCards?.[0], index: MAP_DOT_INDEX } as DealCard;

          setTimeout(() => {
            showPopupCard(
              hotelId,
              getPopupCardProps({
                dealCard: popupDealCard,
                isDot: true,
                commonData,
                localization,
                curiosityParams,
                mapRef,
                isMobile,
                handlePopupCardHotelChosen,
                isLoading,
                isWaiting,
              })
            );
          }, 10);
        }
      }
    });
  };

  const markerClickRef = useRef(markerClick);
  markerClickRef.current = markerClick;
  const stableMarkerClick = useRef((args: MarkerClickProps) => markerClickRef.current(args)).current;

  const maxZoom = 18;
  const markers = useMemo(
    () =>
      hasResults &&
      results
        .filter((dealCard) => !dealCard.product?.rate?.flags?.tsh)
        .map((dealCard) => {
          const { hotelId } = dealCard.product.accommodation;

          return (
            <MarkerComponent
              key={`marker-${hotelId}`}
              isWaiting={isWaiting}
              dealCard={dealCard}
              mapRef={mapRef}
              mouseOnMarker={mouseOnMarker}
              showPopupCard={showPopupCard}
              initialHighlightHotel={initialHighlightHotel}
            />
          );
        }),
    [results, isWaiting]
  );

  const markersFlashSales = useMemo(
    () =>
      dotsGeoJSON?.features &&
      !isLoading &&
      hasResults &&
      dotsGeoJSON?.features
        .filter((feature) => feature?.properties?.price && feature.properties?.flashSale)
        .map((feature) => {
          const { icon } = getPromotionConfig(PROMOTION_TYPES.FLASH_SALES, lastMinuteDeals, isEvoLayout);
          const dotPrice = getDotPrice({
            dot: feature.properties as Dot,
            searchInfoData,
            localization,
            showTotalPrice: showTotalPriceOnDots,
            travellersCount,
          });
          const handleClick = (e: MarkerEvent<MouseEvent>) => {
            e?.originalEvent?.stopPropagation();
            if (!isWaiting && feature?.properties) {
              markerClick({
                hotelId: feature.properties.hotelId,
                cityId: feature.properties.cityId,
                latMarker: feature.properties.lat,
                lonMarker: feature.properties.lng,
                price: feature.properties.price,
              });
            }
          };

          return (
            <Marker
              latitude={feature.properties?.lat}
              longitude={feature.properties?.lng}
              anchor="bottom"
              key={`marker-fs-${feature.properties?.hotelId}`}
              data-id={feature.properties?.hotelId}
              offset={[-0.8, -7]}
              style={{ zIndex: 3 }}
              onClick={handleClick}
            >
              <MarkerContainer
                ref={markerRef}
                $hasIcon={true}
                onMouseMove={(e) => e.stopPropagation()}
                className="marker marker_promotion_flash_sales has-icon"
                lastMinuteDeals={lastMinuteDeals}
                promotionType={PROMOTION_TYPES.FLASH_SALES}
              >
                <Promotion icon={icon} isWaiting={isWaiting} promotionType={PROMOTION_TYPES.FLASH_SALES} />
                <Price price={dotPrice} isWaiting={false} />
              </MarkerContainer>
            </Marker>
          );
        }),
    [results, isWaiting, showTotalPriceOnDots, travellersCount]
  );

  const markerDot = useMemo(
    () =>
      resultDot && (
        <MarkerComponent
          key={`marker-dot-${resultDot.hotelId}`}
          isWaiting={isWaiting}
          dot={resultDot}
          mapRef={mapRef}
          mouseOnMarker={mouseOnMarker}
          showPopupCard={showPopupCard}
          setResultDot={setResultDot}
          markerClick={markerClick}
          initialHighlightHotel={undefined}
        />
      ),
    [resultDot, isWaiting]
  );

  const airports = productsData.transport?.airports;
  const multiAirportsSelected = Array.isArray(airports) && airports?.length > 1 && airports.every((a) => a.selected);
  const airportComponents = useMemo(
    () =>
      isTransportEnabled && Array.isArray(airports)
        ? airports.map((airport) => (
            <AirportComponent
              key={`marker-${airport.iata}`}
              userSelected={airport.iata === airportSelected?.iata}
              airport={airport}
              multiAirportsSelected={multiAirportsSelected}
              mapStyleType={mapStyleType}
              setAirportSelected={setAirportSelected}
            />
          ))
        : null,
    [productsData.transport?.airports, airportSelected, isWaiting, mapStyleType]
  );

  const skilifts = useMemo(
    () =>
      !isWaiting &&
      dynamicContentResponse &&
      dynamicContentResponse.skilifts?.map((x) => (
        <SkiliftMarker
          key={`marker-${x.label}`}
          skilift={x}
          setSelectedSkilift={setSelectedSkilift}
          selectedSkilift={selectedSkilift}
        />
      )),
    [dynamicContentResponse, selectedSkilift, isWaiting, isLoading]
  );

  const handleZoomStart = () => {
    if (!isLoading && !isWaiting) {
      setIsResizing(false);
    }
  };

  const handleMoveEnd = (e: ViewStateChangeEventWithOriginal) => {
    // SLIDERS-1870: Handle resetting the search in the parent component
    if (e?.originalEvent?.type === 'resize') return;

    const currentZoom = mapRef?.current?.getZoom() || 0;

    // Ignore map moves while a search is in flight so a pan during loading
    // does not launch a map_area search that overrides the new destination.
    if (isWaiting || isLoading) {
      setZoomLevel(currentZoom);
      return;
    }

    if (
      !isResizing &&
      e.originalEvent &&
      (e.originalEvent.type === 'mouseup' ||
        e.originalEvent.type === 'click' ||
        e.originalEvent.type === 'wheel' ||
        e.originalEvent.type === 'touchend')
    ) {
      const searchParamsFresh = getSearchParams();
      const zoomIn = currentZoom > zoomLevel;
      const zoomOut = currentZoom < zoomLevel;
      if (e.originalEvent.which === 1 || currentZoom === zoomLevel) {
        curiosityClickEvents.mapPanning();
        starTrack.resultPageMapAction(
          createResultPageMapActionPayload({
            searchParams: searchParamsFresh,
            clickEventName: ClickEventNameEnum.MapPanning,
            localization,
          })
        );
      }

      if (zoomIn) {
        curiosityClickEvents.mapZoomIn();
        starTrack.resultPageMapAction(
          createResultPageMapActionPayload({
            searchParams: searchParamsFresh,
            clickEventName: ClickEventNameEnum.MapZoomIn,
            localization,
          })
        );
      }
      if (zoomOut) {
        curiosityClickEvents.mapZoomOut();
        starTrack.resultPageMapAction(
          createResultPageMapActionPayload({
            searchParams: searchParamsFresh,
            clickEventName: ClickEventNameEnum.MapZoomOut,
            localization,
          })
        );
      }
    }

    setZoomLevel(currentZoom);
    if (showFilters) {
      setIsResizing(true);
    }

    if (e?.originalEvent && !isResizing && !showFilters) {
      const searchParamsFresh = getSearchParams({ exclude: ['page'] });

      const newSearchParams = {
        ...searchParamsFresh,
        ...createMapSearchUrlParam(),
      };
      handleMapChanged({ searchParams: newSearchParams, shouldLaunchSearchChanged });
    }
  };

  const handleMoveEndRef = useRef(handleMoveEnd);
  handleMoveEndRef.current = handleMoveEnd;

  const throttledHandlerRef = useRef(
    throttleFn((e: ViewStateChangeEventWithOriginal) => {
      handleMoveEndRef.current(e);
    }, 1000)
  );

  useEffect(
    () => () => {
      if (throttledHandlerRef.current?.cancel) {
        throttledHandlerRef.current.cancel();
      }
    },
    []
  );

  const handleAirportClicked = (iata: string) => {
    curiosityClickEvents.mapSelectAirport(iata || undefined);

    const searchParamsFresh = getSearchParams({ exclude: ['page'] });
    searchParamsFresh[URLParameters.PRE_FILTER_DESTINATION_AIRPORT] = iata;
    delete searchParamsFresh[URLParameters.TRANSPORT_ID];
    const newSearchParams = { ...searchParamsFresh, ...createMapSearchUrlParam() };
    handleMapChanged({ searchParams: newSearchParams, shouldLaunchSearchChanged });
  };

  const handleDragStart = () => {
    setIsResizing(false);
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const handleOnClick = (e: any) => {
    if (e.features && e.features[0]?.layer.id === 'hotels_dots') {
      if (!isWaiting) {
        markerClick({
          hotelId: e.features[0].properties.hotelId,
          cityId: e.features[0].properties.cityId,
          latMarker: e.features[0].properties.lat,
          lonMarker: e.features[0].properties.lng,
          price: e.features[0].properties.price,
        });
        curiosityClickEvents.mapHotelDotClick();
      }
    } else {
      hideAllPopupCards();
      if (airportSelected) setAirportSelected(undefined);
      if (selectedSkilift) setSelectedSkilift(undefined);
      if (isResizing) setIsResizing(false);
      if (isMobile) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const handleOnMouseMove = (e: any) => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      if (e.features && e.features[0]?.layer.id === 'hotels_dots') {
        map.getCanvas().style.cursor = 'pointer';
        if (e.features && (!resultDot || resultDot.hotelId !== e.features[0].properties.hotelId)) {
          const dot = { ...e.features[0].properties, index: MAP_DOT_INDEX } as Dot;
          setResultDot(dot);
        }
      }
    }
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const handleOnMouseLeave = (e: any) => {
    if (e.features && e.features[0]?.layer.id === 'hotels_dots') {
      const map = mapRef?.current?.getMap?.();
      if (map) {
        map.getCanvas().style.cursor = '';
      }
      setTimeout(() => {
        if (!mouseOnMarker.current) {
          setResultDot(undefined);
        }
      }, 100);
    }
  };

  const toggleMapOnTop = (onTop: boolean) => {
    if (onTop) {
      setTimeout(() => setIsMapOnTop(false), 300);
    } else {
      setIsMapOnTop(true);
    }
  };

  const handleExpand = () => {
    setExpand((val) => {
      toggleMapOnTop(val);
      return !val;
    });
  };

  const mapStyleClicked = () => {
    const curiosityClickEvent =
      mapStyleType === MAP_STYLE_TYPE.DEFAULT
        ? curiosityClickEvents.mapStyleTypeSatellite
        : curiosityClickEvents.mapStyleTypeDefault;
    curiosityClickEvent();
    setMapStyleType((prev) => (prev === MAP_STYLE_TYPE.DEFAULT ? MAP_STYLE_TYPE.SATELLITE : MAP_STYLE_TYPE.DEFAULT));
  };

  const showAirportsClicked = () => {
    curiosityClickEvents.mapShowAirports();

    const noResults = productsData?.dealCards?.length === 0;
    if (noResults) {
      setIsResizing(true);
      const searchParamsResetMap = getSearchParams({ exclude: [URLParameters.MAP_AREA] });
      const newSearchParams = createNewSearchParamsBase({ searchParams: searchParamsResetMap });

      handleMapChanged({ searchParams: newSearchParams, shouldLaunchSearchChanged });
      return;
    }

    if (hasAirportsOrStations(productsData) && mapRef?.current) {
      const trimZeros = (nums: number[]): number[] => nums.filter((num) => num !== 0);

      const airportList = productsData.transport?.airports as TransportAirport[];
      const airportsLons: number[] = airportList.map((a) => a.lon);
      const airportsLats: number[] = airportList.map((a) => a.lat);
      mapRef.current.fitBounds(
        [
          [Math.min(...trimZeros([...airportsLons, minLng])), Math.min(...trimZeros([...airportsLats, minLat]))],
          [Math.max(...trimZeros([...airportsLons, maxLng])), Math.max(...trimZeros([...airportsLats, maxLat]))],
        ],
        { padding: 80, duration: 1000 }
      );
    }
  };

  const handleOnLoad = () => {
    setIsMapLoaded(true);
    if (dealCards?.length === 0 && !lat) {
      const searchParamsFresh = getSearchParams();
      handleMapLoad({
        searchInfoDetailData,
        reloadType: ReloadType.SEARCH,
        mapRef,
        searchParams: searchParamsFresh,
        dealCards,
        coordinates,
        setIsResizing,
        isMobile,
      });
    }
    const currentZoom = mapRef?.current?.getZoom();

    setTimeout(() => {
      if (currentZoom) {
        setZoomLevel(currentZoom);
      }
    }, 300);

    if (localizeMap) {
      changeMapLanguage(localization.lang, mapRef);
    }
  };

  useEffect(() => {
    if (isMobile) {
      window.addEventListener('resize', hideAllPopupCards);
      return () => window.removeEventListener('resize', hideAllPopupCards);
    }

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' &&
      new ResizeObserver(() => {
        setIsResizing(true);
        hideAllPopupCards();
        if (mapRef && mapRef.current) {
          mapRef.current.resize();
        }
      });
    if (mapContainerRef.current && resizeObserver) {
      resizeObserver.observe(mapContainerRef.current);
    }
    return () => {
      if (mapContainerRef.current && resizeObserver) {
        resizeObserver.unobserve(mapContainerRef.current);
      }
    };
  }, [canUseDOM, isMobile, mapRef]);

  useEffect(() => {
    if (isLoading) {
      hideAllPopupCards();
      setAirportSelected(undefined);
      setSelectedSkilift(undefined);
    }
  }, [isLoading]);

  // The map mounts with the "View on Map" hotel, whose highlight clears itself before a slow map has loaded.
  const viewOnMapHotelIdRef = useRef(highlightedHotelId);
  useEffect(() => {
    const hotelId = viewOnMapHotelIdRef.current;
    if (!isMapLoaded || !hotelId) return undefined;

    const dealCard = results.find((card) => card.product.accommodation.hotelId === hotelId);
    if (!dealCard) return undefined;

    const timer = setTimeout(() => {
      viewOnMapHotelIdRef.current = null;
      markerSelection(hotelId, true);
      showPopupCard(
        hotelId,
        getPopupCardProps({
          dealCard,
          isDot: false,
          commonData,
          localization,
          curiosityParams,
          mapRef,
          isMobile,
          handlePopupCardHotelChosen,
          isWaiting,
          isLoading,
        })
      );
      if (!isDesktop) window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 10);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapLoaded]);

  useEffect(() => {
    if (canUseDOM) {
      markMarkerWithOpenPopup(popupCardPropsByHotelId.find((popupCard) => popupCard.visible)?.hotelId);
    }
  }, [popupCardPropsByHotelId]);

  // MPH-261: the popup card is a dialog, and closing it takes its focused close button out of the DOM.
  // Focus goes back to the map, which is in the tab order. Markers cannot be operated from the keyboard
  // yet, so focusing the one that opened the card would strand focus on it; that moves there with MPH-255.
  const isPopupCardOpen = popupCardPropsByHotelId.some((p) => p.visible);
  const wasPopupCardOpenRef = useRef(false);
  useEffect(() => {
    const closed = wasPopupCardOpenRef.current && !isPopupCardOpen;
    wasPopupCardOpenRef.current = isPopupCardOpen;
    if (closed && isFocusLost()) mapRef.current?.getCanvas().focus({ preventScroll: true });
  }, [isPopupCardOpen]);

  useEffect((): (() => void) | undefined => {
    const map = mapRef.current;
    if (!map) return undefined;

    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const handleSymbolDotClick = (e: any) => {
      if (e.features && e.features.length > 0 && !isWaiting) {
        const prop = e.features[0].properties;
        stableMarkerClick({
          hotelId: prop?.hotelId,
          cityId: prop?.cityId,
          latMarker: prop?.lat,
          lonMarker: prop?.lng,
          price: prop?.price,
        });
      }
    };

    map.on('mouseenter', 'symbol_dots', handleMouseEnter);
    map.on('mouseleave', 'symbol_dots', handleMouseLeave);
    map.on('click', 'symbol_dots', handleSymbolDotClick);

    return () => {
      map.off('mouseenter', 'symbol_dots', handleMouseEnter);
      map.off('mouseleave', 'symbol_dots', handleMouseLeave);
      map.off('click', 'symbol_dots', handleSymbolDotClick);
    };
  }, [isWaiting, stableMarkerClick, isMapLoaded]);

  const value = useMemo(
    () => ({
      travellersCount,
      searchParams,
      isWaiting,
      isLoading,
      listView: false,
      isWhiteLabel,
    }),
    [travellersCount, searchParams, isWaiting, isLoading, isWhiteLabel]
  );

  const topSecretCircleGeoJSON: Feature<Point, GeoJsonProperties> | null = useMemo(() => {
    if (!resultTSHCardOver && !initialHighlightHotel?.isTopSecret) return null;
    const [longitude, latitude] = [
      resultTSHCardOver?.product?.accommodation?.longitude || initialHighlightHotel?.longitude,
      resultTSHCardOver?.product?.accommodation?.latitude || initialHighlightHotel?.latitude,
    ];
    if (!latitude || !longitude) return null;
    // MPH-1048: drawn around a point offset from the property rather than the property itself. The
    // circle is meant to read as "somewhere in this area", but centred on the hotel it pointed
    // straight at the building and gave away the one thing a Top Secret rate withholds.
    const center = tshAreaCenter({ latitude, longitude });
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: [center.longitude, center.latitude],
      },
    };
  }, [
    resultTSHCardOver,
    initialHighlightHotel?.isTopSecret,
    initialHighlightHotel?.latitude,
    initialHighlightHotel?.longitude,
  ]);

  const visiblePopupCard = popupCardPropsByHotelId.find((p) => p.visible);
  const isDot = !!visiblePopupCard?.props?.isDot;
  const popupCardClassName = sumClasses([
    'map-overlay-popup',
    isDot ? 'map-dot-popup' : '',
    isMobile ? 'map-overlay-popup-mobile' : '',
    resultDot ? 'map-overlay-dot' : '',
  ]);
  const popupCard = visiblePopupCard ? (
    <Popup
      closeButton={false}
      closeOnClick={false}
      maxWidth={visiblePopupCard.props.cardWidth}
      longitude={visiblePopupCard.props.coordinates.lng}
      latitude={visiblePopupCard.props.coordinates.lat}
      className={popupCardClassName}
      {...(isMobile ? { anchor: 'bottom' } : { offset: 0 })}
    >
      <BlankContainer evolutionResultsOnlyEnabled={isEvoLayout}>
        {visiblePopupCard.props.mapProps && (
          <CardsContext.Provider value={value}>
            {isEvoLayout ? (
              <ErrorBoundary key={`evo-popup-${visiblePopupCard.hotelId}`} id="EvolutionCard">
                <EvolutionCard {...visiblePopupCard.props.mapProps} onClose={hideAllPopupCards} />
              </ErrorBoundary>
            ) : (
              <ErrorBoundary key={`popup-${visiblePopupCard.hotelId}`} id="Card">
                <Card {...visiblePopupCard.props.mapProps} onClose={hideAllPopupCards} />
              </ErrorBoundary>
            )}
          </CardsContext.Provider>
        )}
      </BlankContainer>
    </Popup>
  ) : null;

  return (
    <Map
      ref={mapRef}
      initialViewState={{
        longitude: initialCenter.longitude,
        latitude: initialCenter.latitude,
        ...(hasResults && !initialHighlightHotel
          ? {
              bounds: [minLng, minLat, maxLng, maxLat],
              fitBoundsOptions: {
                padding: 50,
                offset: [0, 0],
              },
            }
          : {}),
        ...(initialMapZoom && { zoom: initialMapZoom }),
      }}
      maxZoom={maxZoom}
      minZoom={1}
      mapStyle={MAP_STYLES[mapStyleType].type}
      mapboxAccessToken={mapboxAccessToken}
      projection="mercator"
      onLoad={handleOnLoad}
      onMoveEnd={throttledHandlerRef.current}
      onZoomStart={handleZoomStart}
      onClick={handleOnClick}
      onDragStart={handleDragStart}
      interactiveLayerIds={hasResults ? ['hotels_dots'] : []}
      onMouseMove={handleOnMouseMove}
      onMouseLeave={handleOnMouseLeave}
      dragRotate={false}
      attributionControl={!isEvoLayout}
    >
      {markers}
      {markersFlashSales}
      {markerDot}
      {airportComponents}
      {skilifts}
      <MapControls
        expand={expand}
        mapStyleType={mapStyleType}
        isTransportEnabled={isTransportEnabled}
        isLoading={isLoading}
        productsResultsBeforeMapfilterCnt={productsResultsBeforeMapFilterCnt}
        handleExpand={handleExpand}
        handleResetMap={handleResetMap}
        mapStyleClicked={mapStyleClicked}
        hasAirportsAndStations={hasAirportsAndStations(productsData)}
        hasAirportsOnly={hasAirportsOnly(productsData)}
        hasStationsOnly={hasStationsOnly(productsData)}
        showCloseResize={showCloseResize}
        isDialog={isDialog}
        showAirportsClicked={showAirportsClicked}
      />
      {hasResults && (
        <>
          <Source id="dots" type="geojson" data={dotsGeoJSON}>
            <Layer {...layerStyle} />
          </Source>
          {topSecretCircleGeoJSON && (
            <Source id="circle" type="geojson" data={topSecretCircleGeoJSON}>
              <Layer
                {...hotelTopSecretCircleLayerStyle({
                  latitude: topSecretCircleGeoJSON.geometry.coordinates[1],
                  longitude: topSecretCircleGeoJSON.geometry.coordinates[0],
                })}
              />
            </Source>
          )}
        </>
      )}

      <NavigationControl showCompass={false} />
      {isEvoLayout ? (
        // MPH-981: the detail dialog moves the credits to the bottom-left corner, clear of the controls stacked
        // on its right edge — the full strip on desktop, the compact ⓘ on mobile. `undefined` leaves Mapbox
        // to pick compact by map width everywhere else.
        <AttributionControl
          position={isDesktop && !isDialog ? 'bottom-right' : 'bottom-left'}
          compact={isDialog ? !isDesktop : undefined}
        />
      ) : (
        <>
          <ScaleControl position={'bottom-left'} unit={isUsLocale(localization.locale) ? 'imperial' : 'metric'} />
          <ScaleControl position={'bottom-right'} unit={isUsLocale(localization.locale) ? 'imperial' : 'metric'} />
        </>
      )}
      {popupCard}
      {airportSelected && (
        <Popup
          closeButton={false}
          closeOnClick={false}
          longitude={airportSelected.lon}
          latitude={airportSelected.lat}
          className="airport-popup"
        >
          <AirportPopup
            {...createAirportPopupProps({
              airportSelected,
              multiAirportsSelected,
              handleAirportClicked,
            })}
          />
        </Popup>
      )}
      {selectedSkilift && (
        <Popup
          closeButton={false}
          closeOnClick={false}
          longitude={selectedSkilift.lon}
          latitude={selectedSkilift.lat}
          offset={[0, -25] as [number, number]}
          className="skilift-popup"
        >
          <div>{selectedSkilift.label}</div>
          <span>{t('vacation.oss.map.skilift')}</span>
        </Popup>
      )}
    </Map>
  );
};

export default MapComponent;

function getEmptyProductDataForInitialHighlightHotel(initialHighlightHotel: InitialHighlightHotel): ProductsData {
  return {
    dealCards: [getEmptyDealCardForInitialHighlightHotel(initialHighlightHotel)],

    dots: [],

    apiStatus: APIStatusEnum.READY,

    productCounters: {
      productsCount: 1,
      productsCountAlternative: 0,
      productsCountBookNowPayLater: 0,
      productsCountTo: 0,
      productsPackageDiscountAvailable: 0,
      productsPackageDiscountEnabled: 0,
      productsPriceStats: {
        absoluteCheapestPrice: 0,
        cheapestPriceFirstPage: 0,
        firstPriceBySorting: 0,
      },
      productsResultsBeforeMapFilterCnt: 0,
      productsCountPlus: 0,
    },
    message: '',
    businessProfileSearchResponse: '',
    rankingBusinessProfile: '',
    defaultSearch: {
      destination: '',
      dateFrom: '',
      dateTo: '',
      adults: '',
      sort: SortValues.Recommended,
      origin: '',
    },
    debugData: {},
    instantSearch: false,
    apexAvailabilityFlag: false,
  };
}

export function getEmptyDealCardForInitialHighlightHotel(initialHighlightHotel: InitialHighlightHotel): DealCard {
  return {
    index: 0,
    deepLink: '',
    promotionTypes: [],
    promotionTheme: null,
    // Mirror buildDealCards: carry the per-hotel review provider so the popup's <Ratings> can pick
    // the TY/TA layout (and hide when unknown) instead of silently losing the score.
    reviewSource: initialHighlightHotel.ratingSource,

    product: {
      accommodation: initialHighlightHotel,
      dates: { alternative: false, soldOut: false },
      geo: {
        cityDisplayName: '',
        cityName: '',
        countryCode: '',
        countryName: '',
        originName: '',
        destinationName: '',
        distanceKm: 0,
        internalIdCity: 0,
        leisure: 0,
        tagId: 0,
        tagIds: '',
      },
      promotions: {
        blackFriday: false,
        flashSale: false,
        flashSaleExpiration: '',
        londonEye: false,
        tourFrance: false,
      },
      rate: {
        features: {
          cancellation: true,
          bookNowPayLater: true,
          mealPlan: 1,
          rooms: 1,
        },
        flags: {
          apex: false,
          fallback: false,
          livectx: false,
          mobile: false,
          myPrice: false,
          opx: false,
          pinkPeople: false,
          tmpStatefulSendSearchId: true,
          tsh: initialHighlightHotel.isTopSecret ?? false,
          voucherExtraType: 0,
          plus: false,
          loyalty: false,
        },
        offers: {
          januarySale: false,
          skiPass: false,
          spa: false,
          tickets: false,
          transfer: false,
        },
        packageDiscount: {
          enabled: false,
          totalPriceFo: 0,
          totalPriceHo: 0,
        },
        price: {
          age: 0,
          currency: initialHighlightHotel.currency,
          isPerPerson: true,
          depositAmount: 0,
          price: initialHighlightHotel.cheapestMealPlanFinalPrice,
          showDiscount: false,
          strikethrough: 0,
          taxes: 0,
          taxesTotal: 0,
          total: {
            depositAmount: 0,
            price: initialHighlightHotel.cheapestMealPlanFinalPrice,
            strikethrough: 0,
            priceEur: 0,
            strikethroughEur: 0,
          },
        },
        tep: {
          cost: initialHighlightHotel.cheapestMealPlanFinalPrice,
          discount: 0,
          enabled: false,
          voucher: 0,
          standardPrice: initialHighlightHotel.cheapestMealPlanFinalPrice,
        },
        pricePlus: {
          age: 0,
          currency: '',
          isPerPerson: false,
          price: 0,
          showDiscount: false,
          strikethrough: 0,
          taxes: 0,
          taxesTotal: 0,
          total: {
            price: 0,
            strikethrough: 0,
          },
        },
      },
    },
  };
}
